import React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";

import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import { formatDate } from "@/lib/formatDate";
import { FeedPost } from "@/__generated__/types";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { Button } from "@/components/ui";
import { RemoveButton } from "@/components/RemoveButton";
import { useDestroyFeedPost } from "@/hooks/useDestroyFeedPost";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";

const DATE_FORMAT = "PPP";
const PLACEHOLDER_FALLBACK = "--";

export const useFeedPostsColumns = ({
  publishDateHeader,
}: {
  publishDateHeader: string;
}): ColumnDef<FeedPost>[] => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { destroyFeedPost, loading: deleteLoading } = useDestroyFeedPost();
  const { navToRoute } = useProtectedNavigation();

  const handleEditClick = (feedPost: FeedPost) => {
    navToRoute("feedPostDetails", { feedPostId: feedPost.id });
  };

  const handleDeleteFeedPost = (feedPost: FeedPost) => {
    if (feedPost?.id) {
      destroyFeedPost(feedPost.id);
    }
  };

  const baseColumns: ColumnDef<FeedPost>[] = [
    {
      id: "name",
      accessorFn: (row: FeedPost) => row?.name || "",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => (
        <DataTable.THeaderCell>
          {t("feedPost.table.columns.name")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<FeedPost> }) => (
        <DataTable.TBodyCell>
          <div className="font-medium">{row.original?.name}</div>
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "publishDate",
      accessorFn: (row: FeedPost) => {
        const { publishDate } = row || {};
        return publishDate
          ? formatDate(new Date(publishDate * 1000), DATE_FORMAT, {
              locale: locale as "en",
            })
          : "";
      },
      enableSorting: false,
      enableColumnFilter: false,
      header: () => (
        <DataTable.THeaderCell>{publishDateHeader}</DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<FeedPost> }) => (
        <DataTable.TBodyCell>
          {row.original?.publishDate
            ? formatDate(
                new Date(row.original.publishDate * 1000),
                DATE_FORMAT,
                {
                  locale: locale as "en",
                },
              )
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "postedBy",
      accessorFn: (row: FeedPost) => {
        const poster = row?.postedBy;
        if (!poster) return "";
        return `${poster.firstName} ${poster.lastName || ""}`.trim();
      },
      enableSorting: false,
      enableColumnFilter: false,
      header: () => (
        <DataTable.THeaderCell>
          {t("feedPost.table.columns.postedBy")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<FeedPost> }) => (
        <DataTable.TBodyCell>
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage
                src={row.original?.postedBy?.profilePhoto?.url || undefined}
                alt={`${row.original?.postedBy?.firstName} ${row.original?.postedBy?.lastName || ""}`}
              />
              <AvatarFallback>
                {(row.original?.postedBy?.firstName || "U").charAt(0)}
                {(row.original?.postedBy?.lastName || "U").charAt(0)}
              </AvatarFallback>
            </Avatar>
            <span>
              {row.original?.postedBy
                ? `${row.original.postedBy.firstName} ${row.original.postedBy.lastName || ""}`
                : PLACEHOLDER_FALLBACK}
            </span>
          </div>
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "ctaEnabled",
      accessorFn: (row: FeedPost) =>
        row.ctaUrl?.length ? t("global.yes") : t("global.no"),
      enableSorting: false,
      enableColumnFilter: true,
      header: () => (
        <DataTable.THeaderCell>
          {t("feedPost.table.columns.ctaEnabled")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<FeedPost> }) => (
        <DataTable.TBodyCell>
          {row.original?.ctaUrl?.length ? t("global.yes") : t("global.no")}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "views",
      accessorFn: (row: FeedPost) => row?.views || 0,
      enableSorting: true,
      enableColumnFilter: false,
      header: () => (
        <DataTable.THeaderCell>
          {t("feedPost.table.columns.views")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<FeedPost> }) => (
        <DataTable.TBodyCell>{row.original?.views || 0}</DataTable.TBodyCell>
      ),
    },
    {
      id: "actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<FeedPost> }) => {
        const feedPost = row.original;
        if (!feedPost) {
          return (
            <DataTable.TBodyCell isLastCell>
              {PLACEHOLDER_FALLBACK}
            </DataTable.TBodyCell>
          );
        }

        return (
          <DataTable.TBodyCell isLastCell>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditClick(feedPost)}
              >
                {t("feedPost.table.scheduled.actions.edit")}
              </Button>

              <RemoveButton
                alertTitle={t(
                  "feedPost.table.scheduled.confirmDialogs.delete.title",
                )}
                alertDescription={t(
                  "feedPost.table.scheduled.confirmDialogs.delete.message",
                )}
                deleteAction={() => handleDeleteFeedPost(feedPost)}
                disabled={deleteLoading}
                loading={deleteLoading}
                size="sm"
                className="h-8 w-8 p-0"
              />
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return baseColumns;
};
